import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Send, MessageSquare, FileText } from "lucide-react";

const TICKET_STATUSES = ["new", "in_progress", "waiting_on_customer", "resolved", "closed"];
let quoteTemplateCache = null;

export function TicketThread({ messages = [], compact = false }) {
  if (!messages.length) return <div className="text-xs text-slate-500">No replies yet.</div>;
  return (
    <ol className={`space-y-2 ${compact ? "max-h-64 overflow-y-auto dg-scroll pr-1" : ""}`} data-testid="ticket-thread">
      {messages.map((m) => (
        <li key={m.id} className={`flex ${m.from === "david" ? "justify-end" : "justify-start"}`}>
          <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap ${m.from === "david" ? "bg-cyan-500/15 border border-cyan-500/30 text-slate-100" : "bg-slate-800/70 text-slate-200"}`} data-testid={`ticket-msg-${m.from}`}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1">{m.from === "david" ? "David" : "Customer"} · {new Date(m.at).toLocaleString()}</div>
            {m.text}
          </div>
        </li>
      ))}
    </ol>
  );
}

export function AdminTicketReply({ ticket, onDone }) {
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState(ticket.status);
  const [busy, setBusy] = useState(false);

  const insertQuote = async () => {
    try {
      if (!quoteTemplateCache) quoteTemplateCache = (await api.get("/admin/settings")).data.quote_template || "";
      const first = (ticket.name || "there").split(" ")[0];
      setMessage(quoteTemplateCache.replace(/\[first name\]/gi, first));
      if (status === "new") setStatus("in_progress");
    } catch { toast.error("Couldn't load the quote template"); }
  };

  const submit = async () => {
    setBusy(true);
    const fd = new FormData();
    try {
      if (message.trim()) {
        fd.append("message", message.trim()); fd.append("status", status);
        await api.post(`/admin/tickets/${ticket.ticket_id}/reply`, fd);
        toast.success("Reply sent to customer");
      } else {
        fd.append("status", status); fd.append("note", "");
        await api.patch(`/admin/tickets/${ticket.ticket_id}`, fd);
        toast.success("Status updated");
      }
      setMessage(""); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-w-[260px] w-full lg:w-[360px]">
      <div className="dg-eyebrow mb-2 flex items-center gap-2"><MessageSquare className="w-3 h-3" /> Conversation</div>
      <TicketThread messages={ticket.messages} compact />
      <div className="mt-3 flex gap-2">
        <select className="dg-input flex-1" value={status} onChange={(e) => setStatus(e.target.value)} data-testid={`ticket-status-${ticket.ticket_id}`}>
          {TICKET_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
      </div>
      <textarea className="dg-input mt-2" rows={3} placeholder="Write your reply to the customer…" value={message} onChange={(e) => setMessage(e.target.value)} data-testid={`ticket-reply-${ticket.ticket_id}`} />
      <button type="button" onClick={insertQuote} className="dg-btn-ghost rounded-full px-3 py-1.5 text-xs mt-2 flex items-center gap-1.5" data-testid={`ticket-quote-${ticket.ticket_id}`}>
        <FileText className="w-3 h-3" /> Insert install quote template
      </button>
      <button onClick={submit} disabled={busy} className="dg-btn-primary rounded-full px-4 py-2 text-sm w-full mt-2 flex items-center justify-center gap-2 disabled:opacity-50" data-testid={`ticket-send-${ticket.ticket_id}`}>
        <Send className="w-4 h-4" /> {busy ? "Sending…" : message.trim() ? "Send reply & email customer" : "Update status only"}
      </button>
    </div>
  );
}
